// Shared scaffolding for all sub-pages (docs, legal, about, contact).
// Same world as the landing page, lower volume: blueprint grid, one soft
// signal glow, mono kicker, display title — then the page's content.
import { useEffect, type CSSProperties, type ReactNode } from 'react';
import { SiteNav } from '../sections/hero';
import { BigFooter } from '../sections/footer';

export function usePageTitle(title: string) {
  useEffect(() => {
    const prev = document.title;
    document.title = `${title} — ClipRadar`;
    return () => { document.title = prev; };
  }, [title]);
}

export function PageShell({ kicker, title, sub, meta, children, wide }: {
  kicker: string;
  title: ReactNode;
  sub?: ReactNode;
  meta?: ReactNode;          // optional mono meta strip under the title (legal pages)
  children: ReactNode;
  wide?: boolean;            // 1060px container (docs) instead of 760px (prose)
}) {
  usePageTitle(kicker);
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SiteNav routed />
      <main style={{ flex: 1, position: 'relative', overflow: 'hidden', padding: '148px 24px 96px' }}>
        {/* atmosphere */}
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'var(--bg-grid)', maskImage: 'linear-gradient(180deg, black, transparent 520px)', WebkitMaskImage: 'linear-gradient(180deg, black, transparent 520px)' }}></div>
          <div style={{ position: 'absolute', top: -260, left: '50%', transform: 'translateX(-50%)', width: 900, height: 520, background: 'radial-gradient(ellipse at center, rgba(74,227,255,0.09), rgba(139,124,255,0.04) 50%, transparent 70%)', filter: 'blur(30px)' }}></div>
        </div>
        <div className="crw-page-in" style={{ position: 'relative', maxWidth: wide ? 1080 : 760, margin: '0 auto' }}>
          <header style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: wide ? 48 : 40 }}>
            <span className="cr-label" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--signal-400)' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--signal-400)', boxShadow: '0 0 8px var(--signal-glow)' }}></span>
              {kicker}
            </span>
            <h1 className="crw-h-grad" style={{ margin: 0, fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(32px, 4.6vw, 52px)', letterSpacing: 'var(--tracking-display)', lineHeight: 1.08, textWrap: 'balance' }}>
              {title}
            </h1>
            {sub ? <p style={{ margin: 0, maxWidth: 560, fontSize: 15, lineHeight: 1.7, color: 'var(--fg-2)', textWrap: 'pretty' }}>{sub}</p> : null}
            {meta ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>{meta}</div>
            ) : null}
          </header>
          {children}
        </div>
      </main>
      <BigFooter />
    </div>
  );
}

/* mono meta chip — "LAST UPDATED · JUNE 2026" etc. */
export function MetaChip({ children }: { children: ReactNode }) {
  return (
    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--fg-3)', padding: '4px 9px', borderRadius: 6, border: '1px solid var(--line-2)', background: 'var(--surface-card)' }}>
      {children}
    </span>
  );
}

/* numbered article section — the "signal protocol document" look of legal pages */
export function DocSection({ n, title, children, id }: { n?: string; title: string; children: ReactNode; id?: string }) {
  return (
    <section id={id} style={{ borderTop: '1px solid var(--line-1)', padding: '26px 0 10px', scrollMarginTop: 110 }}>
      <h2 style={{ display: 'flex', alignItems: 'baseline', gap: 12, margin: '0 0 12px', fontFamily: 'var(--font-display)', fontSize: 19, fontWeight: 600, letterSpacing: 'var(--tracking-heading)', color: 'var(--fg-1)' }}>
        {n ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.08em', color: 'var(--signal-400)' }}>{n}</span> : null}
        {title}
      </h2>
      <div className="crw-prose">{children}</div>
    </section>
  );
}

/* tip / warning callout for docs */
export function Callout({ kind = 'tip', children }: { kind?: 'tip' | 'warn'; children: ReactNode }) {
  const warn = kind === 'warn';
  return (
    <div style={{
      display: 'flex', gap: 10, padding: '12px 14px', margin: '14px 0', borderRadius: 10, fontSize: 13.5, lineHeight: 1.6,
      color: warn ? 'var(--warning)' : 'var(--signal-300)',
      background: warn ? 'var(--warning-dim)' : 'var(--signal-dim)',
      border: `1px solid ${warn ? 'rgba(255,196,77,0.25)' : 'rgba(74,227,255,0.22)'}`,
    }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.08em', paddingTop: 2 }}>{warn ? 'NOTE' : 'TIP'}</span>
      <span style={{ color: 'var(--fg-2)' }}>{children}</span>
    </div>
  );
}

export const kbd: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 12, padding: '1px 6px', borderRadius: 5,
  border: '1px solid var(--line-2)', background: 'var(--surface-raised)', color: 'var(--fg-1)', whiteSpace: 'nowrap',
};
